import {
  FC,
  HTMLAttributes,
  useEffect,
  useRef
} from 'react';
import { RootRoute } from '@app/const';
import { useCanvasTabs, useRootRoute } from '@app/hooks';
import {
  AppFooter,
  AppHeader,
  CanvasChrome,
  LeftPanels,
  RightPanels
} from '@app/page-components/app';
import { AppCanvasPage } from './canvas/page';
import styles from './layout.module.scss';

type GetPageContainerProps = {
  className?: string;
  route: RootRoute;
};

export const AppLayout: FC = () => {
  const initialized = useCanvasTabs((s) => s.initialized);
  const [activeRoute] = useRootRoute();
  const appMainRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Fix invalid hashes without adding a history entry.
    if (activeRoute !== location.hash.slice(1)) {
      const { pathname, search } = location;
      history.replaceState(null, '', `${pathname}${search}#${activeRoute}`);
    }

    const timeoutId = setTimeout(() => appMainRef.current?.scrollTo(0, 0), 100);
    return () => clearTimeout(timeoutId);
  }, [activeRoute]);

  const getPageContainerProps = ({
    route,
    className
  }: GetPageContainerProps): HTMLAttributes<HTMLDivElement> => {
    return {
      className: className ?? 'page',
      id: route,
      style: { display: (activeRoute === route) ? undefined : 'none' }
    };
  };

  return (
    <>
      <div
        id="app-layout"
        className={styles.appLayout}
        data-page={activeRoute}
      >
        {(!initialized) ? null : <AppHeader />}
        {(!initialized) ? null : (
          <CanvasChrome hidden={activeRoute !== RootRoute.canvas} />
        )}
        <main
          className={styles.main}
          id="app-main"
          ref={appMainRef}
        >
          <div {...getPageContainerProps({
            className: styles.canvasHost,
            route: RootRoute.canvas
          })}>
            <LeftPanels />
            {(!initialized) ? null : <AppCanvasPage />}
          </div>
          <RightPanels />
          <div
            {...getPageContainerProps({
              className: 'page overflow-hidden',
              route: RootRoute.agentic
            })}
          />
          <div {...getPageContainerProps({ route: RootRoute.intents })} />
          <div {...getPageContainerProps({ route: RootRoute.settings })} />
          <div {...getPageContainerProps({ route: RootRoute.save })} />
          <div {...getPageContainerProps({ route: RootRoute.testTool })} />
        </main>

        <AppFooter />
      </div>
      <div className="preview_wrapper">
        <div className="preview_container" />
      </div>
      <div className="link_wrapper">
        <div className="link_container" />
      </div>
      <div className="context_menu" />
      <div className="designer-version-container" />
    </>
  );
};
